/**
 * Upgrades. Three families per the plan: a pair of doublers per dinosaur tier
 * (templated from DINOS, so a new tier gets its upgrades for free), click
 * upgrades, and global EPS multipliers. Costs are a starting point for balance.
 */

import { DINOS } from './dinos';
import type { Condition } from '../game/conditions';

export type UpgradeEffect =
  | { kind: 'dinoMult'; dinoId: string; mult: number }
  | { kind: 'clickFlat'; amount: number }
  /** Adds this fraction of current EPS to each click. */
  | { kind: 'clickEpsPct'; pct: number }
  | { kind: 'globalMult'; mult: number }
  /** Adds this fraction to the offline rate (0.1 = ten percentage points). */
  | { kind: 'offlineRate'; add: number };

export interface UpgradeDef {
  id: string;
  name: string;
  description: string;
  cost: number;
  effect: UpgradeEffect;
  unlock: Condition;
}

/** Doubler costs as multiples of the tier's base price: 10× at level 1, 50× at level 10. */
const DOUBLER_I_COST = 10;
const DOUBLER_II_COST = 50;

const dinoUpgrades: UpgradeDef[] = DINOS.flatMap((d) => [
  {
    id: `dino:${d.id}:1`,
    name: `Cozy ${d.name} nests`,
    description: `${d.name} lay twice as many eggs.`,
    cost: d.baseCost * DOUBLER_I_COST,
    effect: { kind: 'dinoMult', dinoId: d.id, mult: 2 },
    unlock: { kind: 'dinoLevel', dinoId: d.id, level: 1 },
  },
  {
    id: `dino:${d.id}:2`,
    name: `${d.name} growth spurt`,
    description: `${d.name} lay twice as many eggs, again.`,
    cost: d.baseCost * DOUBLER_II_COST,
    effect: { kind: 'dinoMult', dinoId: d.id, mult: 2 },
    unlock: { kind: 'dinoLevel', dinoId: d.id, level: 10 },
  },
]);

const clickUpgrades: UpgradeDef[] = [
  {
    id: 'click:warm-hands',
    name: 'Warm Hands',
    description: 'Each click collects 1 more egg.',
    cost: 100,
    effect: { kind: 'clickFlat', amount: 1 },
    unlock: { kind: 'eggsThisEra', amount: 50 },
  },
  {
    id: 'click:padded-nest',
    name: 'Padded Nest',
    description: 'Each click also collects 1% of your eggs per second.',
    cost: 1_000,
    effect: { kind: 'clickEpsPct', pct: 0.01 },
    unlock: { kind: 'eggsThisEra', amount: 500 },
  },
  {
    id: 'click:heat-lamp',
    name: 'Heat Lamp',
    description: 'Each click also collects 2% of your eggs per second.',
    cost: 50_000,
    effect: { kind: 'clickEpsPct', pct: 0.02 },
    unlock: { kind: 'eggsThisEra', amount: 25_000 },
  },
  {
    id: 'click:incubator',
    name: 'Incubator',
    description: 'Each click also collects 5% of your eggs per second.',
    cost: 5_000_000,
    effect: { kind: 'clickEpsPct', pct: 0.05 },
    unlock: { kind: 'eggsThisEra', amount: 2_500_000 },
  },
];

const globalUpgrades: UpgradeDef[] = [
  {
    id: 'global:fertile-valley',
    name: 'Fertile Valley',
    description: 'All dinosaurs lay 5% more eggs.',
    cost: 10_000,
    effect: { kind: 'globalMult', mult: 1.05 },
    unlock: { kind: 'eggsThisEra', amount: 5_000 },
  },
  {
    id: 'global:volcanic-soil',
    name: 'Volcanic Soil',
    description: 'All dinosaurs lay 10% more eggs.',
    cost: 500_000,
    effect: { kind: 'globalMult', mult: 1.1 },
    unlock: { kind: 'eggsThisEra', amount: 250_000 },
  },
  {
    id: 'global:long-summer',
    name: 'Long Summer',
    description: 'All dinosaurs lay 15% more eggs.',
    cost: 50_000_000,
    effect: { kind: 'globalMult', mult: 1.15 },
    unlock: { kind: 'eggsThisEra', amount: 25_000_000 },
  },
  {
    id: 'global:dino-daycare',
    name: 'Dino Daycare',
    description: 'All dinosaurs lay 25% more eggs.',
    cost: 5_000_000_000,
    effect: { kind: 'globalMult', mult: 1.25 },
    unlock: { kind: 'eggsThisEra', amount: 2_500_000_000 },
  },
];

/**
 * Late multipliers, so production keeps climbing once the doublers are
 * bought (M7 balance: without them EPS stalls past tier 10 and the last
 * tiers never arrive). Deliberately only two, leaving the top of the curve
 * for later late-game systems.
 */
const lateUpgrades: UpgradeDef[] = [
  {
    id: 'global:warm-springs',
    name: 'Warm Springs',
    description: 'All dinosaurs lay 50% more eggs.',
    cost: 20_000_000_000,
    effect: { kind: 'globalMult', mult: 1.5 },
    unlock: { kind: 'eggsThisEra', amount: 10_000_000_000 },
  },
  {
    id: 'global:fertile-crescent',
    name: 'Fertile Crescent',
    description: 'All dinosaurs lay twice as many eggs.',
    cost: 2_000_000_000_000,
    effect: { kind: 'globalMult', mult: 2 },
    unlock: { kind: 'eggsThisEra', amount: 1_000_000_000_000 },
  },
];

/** Away-rate upgrades: the away rate starts at 10% of eggs per second; each adds ten points. */
const offlineUpgrades: UpgradeDef[] = [
  {
    id: 'offline:night-nest',
    name: 'Night Nest',
    description: 'Away rate +10%: your dinosaurs lay more while the game is closed.',
    cost: 5_000_000,
    effect: { kind: 'offlineRate', add: 0.1 },
    unlock: { kind: 'eggsThisEra', amount: 1_000_000 },
  },
  {
    id: 'offline:moonlit-valley',
    name: 'Moonlit Valley',
    description: 'Away rate +10%: your dinosaurs lay more while the game is closed.',
    cost: 5_000_000_000,
    effect: { kind: 'offlineRate', add: 0.1 },
    unlock: { kind: 'eggsThisEra', amount: 1_000_000_000 },
  },
  {
    id: 'offline:dino-dreams',
    name: 'Dino Dreams',
    description: 'Away rate +10%: your dinosaurs lay more while the game is closed.',
    cost: 5_000_000_000_000,
    effect: { kind: 'offlineRate', add: 0.1 },
    unlock: { kind: 'eggsThisEra', amount: 1_000_000_000_000 },
  },
];

export const UPGRADES: readonly UpgradeDef[] = [
  ...dinoUpgrades,
  ...clickUpgrades,
  ...globalUpgrades,
  ...lateUpgrades,
  ...offlineUpgrades,
];

export const UPGRADES_BY_ID: ReadonlyMap<string, UpgradeDef> = new Map(
  UPGRADES.map((u) => [u.id, u]),
);
